import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterCategory, ProductBinService } from 'src/app/services/productbin.service';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { ProductReference } from "src/app/models/layout/product-reference";
import { GroupedProductReference } from "src/app/models/grouped-product-reference";
import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem } from '@ng-bootstrap/ng-bootstrap';
import { NgClass, AsyncPipe } from '@angular/common';
import { ProductBinProductComponent } from '../product-bin-product/product-bin-product.component';

export type SortOption = 'Name' | 'Height' | 'Width' | 'Brand';

@Component({
    selector: 'app-product-bin',
    templateUrl: './product-bin.component.html',
    styleUrls: ['./product-bin.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgClass, ProductBinProductComponent, AsyncPipe]
})
export class ProductBinComponent {
    readonly productBinId = 'product-bin';
    connectedLists$: Observable<string[]> | null = null;
    private sortOptionSubject = new BehaviorSubject<SortOption>('Name');

    products$: Observable<ProductReference[]>;
    groupedproducts$: Observable<GroupedProductReference[]>;

    groupBy: string = "";
    sortBy: SortOption = 'Name';

    expandedGroups = new Set<string>();
    showOnLayout = true;
    showOffLayout = true;
    visibleGroups = new Set<string>();

    readonly rangeStatusOptions: string[] = ['Ranged', 'Not Ranged'];
    readonly refreshPlanOptions: string[] = ['Will Add', 'Will Remove'];
    readonly lifecycleOptions: string[] = ['NPI', 'Active', 'Discontinued', 'TB Discontinued'];

    private destroyRef = inject(DestroyRef);

    constructor(
        public productBinService: ProductBinService,
        private cdr: ChangeDetectorRef,
    ) {
        this.products$ = combineLatest([
            this.productBinService.getFilteredProducts(),
            this.sortOptionSubject
        ]).pipe(
            map(([products, sort]) => this.sortProducts(products, sort))
        );

        this.groupedproducts$ = combineLatest([
            this.productBinService.getFilteredProductsByGroup(""),
            this.sortOptionSubject
        ]).pipe(
            map(([groups, sort]) => {
                groups.forEach(g => {
                    g.products = this.sortProducts(g.products, sort);
                });
                return groups.sort((a, b) => a.group.localeCompare(b.group));
            })
        );

        this.groupedproducts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(groups => {
            this.visibleGroups = new Set(groups.map(g => g.group));
            this.cdr.markForCheck();
        });
    }

    private sortProducts(products: ProductReference[], sort: SortOption): ProductReference[] {
        return [...products].sort((a, b) => {
            switch (sort) {
                case 'Name': return a.description.localeCompare(b.description);
                case 'Brand': return a.brand.localeCompare(b.brand);
                case 'Height': return (b.effectiveDimensions?.height || 0) - (a.effectiveDimensions?.height || 0);
                case 'Width': return (b.effectiveDimensions?.width || 0) - (a.effectiveDimensions?.width || 0);
                default: return 0;
            }
        });
    }

    onSortSelected(option: SortOption): void {
        this.sortBy = option;
        this.sortOptionSubject.next(option);
    }

    onSearch(event: Event): void {
        const searchTerm = (event.target as HTMLInputElement).value;
        this.productBinService.setSearchTerm(searchTerm);
    }

    onGroupExpanded(group: string) {
        this.expandedGroups.add(group);
    }

    onGroupCollapsed(group: string) {
        this.expandedGroups.delete(group);
    }

    isGroupExpanded(group: string): boolean {
        return this.expandedGroups.has(group);
    }

    isGroupVisible(group: string): boolean {
        return this.visibleGroups.has(group);
    }

    toggleGroup(group: string): void {
        if (this.isGroupExpanded(group)) {
            this.onGroupCollapsed(group);
        } else {
            this.onGroupExpanded(group);
        }
    }

    toggleGroupVisibility(group: string): void {
        if (this.visibleGroups.has(group)) {
            this.visibleGroups.delete(group);
        } else {
            this.visibleGroups.add(group);
        }
    }

    onGroupBySelected(selectedItem: string): void {
        this.groupBy = selectedItem;
        this.groupedproducts$ = combineLatest([
            this.productBinService.getFilteredProductsByGroup(this.groupBy),
            this.sortOptionSubject
        ]).pipe(
            map(([groups, sort]) => {
                 groups.forEach(g => {
                    g.products = this.sortProducts(g.products, sort);
                });
                return groups.sort((a, b) => a.group.localeCompare(b.group));
            })
        );

         this.groupedproducts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(groups => {
            this.visibleGroups = new Set(groups.map(g => g.group));
            this.cdr.markForCheck();
        });
    }

    onShowOnLayoutChange(event: Event): void {
        this.showOnLayout = (event.target as HTMLInputElement).checked;
        this.productBinService.setShowOnLayout(this.showOnLayout);
    }

    onShowOffLayoutChange(event: Event): void {
        this.showOffLayout = (event.target as HTMLInputElement).checked;
        this.productBinService.setShowOffLayout(this.showOffLayout);
    }

    toggleFilter(category: FilterCategory, value: string): void {
        this.productBinService.toggleFilter(category, value);
    }

    isFilterSelected(category: FilterCategory, value: string): boolean {
        return this.productBinService.isFilterSelected(category, value);
    }

    clearAllFilters(): void {
        this.productBinService.clearAllFilters();
    }

    getActiveFilterCount(): number {
        return this.productBinService.getActiveFilterCount();
    }
}
